import { motion } from "framer-motion";
import { HOW } from "@/constants/testIds";
import { Scan, Cpu, ClipboardList, Wrench } from "lucide-react";

const STEPS = [
  {
    n: "01",
    Icon: Scan,
    title: "Scan the VIN",
    body: "Point your camera at the plate, plug in an OBD-II dongle, or upload a photo. Mymech instantly identifies year, trim, engine, drivetrain, and factory options.",
  },
  {
    n: "02",
    Icon: Cpu,
    title: "AI agents analyze",
    body: "Six specialized agents cross-reference DTC codes, live sensor drift, TSBs, and 12M+ recorded repairs. No single black-box, just precise, transparent reasoning.",
  },
  {
    n: "03",
    Icon: ClipboardList,
    title: "Get a repair plan",
    body: "A prioritized, cost-estimated plan with OEM part numbers, torque specs, and time-to-fix. Printable, shareable, and mechanic-ready.",
  },
  {
    n: "04",
    Icon: Wrench,
    title: "Book or DIY",
    body: "Route to a vetted shop, order the exact parts, or follow the step-by-step DIY workflow with vision-checkpoints and community verification.",
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how"
      data-testid={HOW.section}
      className="relative bg-void py-24 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16 lg:mb-24">
          <div className="lg:col-span-5">
            <span className="chip-brand">/ 02 · workflow</span>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tighter leading-[0.95]">
              A four-step operating<br />
              system for your car.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-white/60 text-base sm:text-lg leading-relaxed font-light">
              We built Mymech around the actual sequence a great mechanic follows:
              observation, reasoning, planning, action. Every step
              legible, fast, and cheap enough to run on any car, anywhere.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className="group relative bg-obsidian p-8 lg:p-10 hover:bg-graphite transition-colors duration-500 min-h-[320px] flex flex-col"
            >
              <div className="flex items-start justify-between mb-8">
                <span className="font-mono-tech text-[11px] tracking-widest text-white/30 uppercase">
                  Step {s.n}
                </span>
                <s.Icon className="h-5 w-5 text-white/40 group-hover:text-mclaren transition-colors" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-2xl font-semibold text-white tracking-tight mb-4">
                {s.title}
              </h3>
              <p className="text-white/55 text-sm leading-relaxed font-light">
                {s.body}
              </p>
              <div className="mt-auto pt-8 flex items-center gap-2 text-mclaren/0 group-hover:text-mclaren transition-colors font-mono-tech text-[11px] uppercase tracking-widest">
                <span className="h-px w-6 bg-mclaren" />
                Ready
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
